// components/models/IdeogramGenerateControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import {
  IdeogramGenerateParams,
  IdeogramMagicPrompt,
  IdeogramRenderingSpeed,
  IdeogramStyleType,
  IdeogramAspectRatio,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';

export const getDefaultIdeogramGenerateParams = (): IdeogramGenerateParams => ({
  prompt: '',
  seed: -1,
  magic_prompt: 'AUTO',
  rendering_speed: 'DEFAULT',
  style_type: 'AUTO',
  negative_prompt: '',
  num_images: 1,
  aspect_ratio: '1x1',
});

// Sentence-case labels for the UI <-> the enums the Ideogram API expects.
const MAGIC_PROMPT_OPTIONS: { label: string; value: IdeogramMagicPrompt }[] = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'On', value: 'ON' },
  { label: 'Off', value: 'OFF' },
];
const RENDERING_SPEED_OPTIONS: { label: string; value: IdeogramRenderingSpeed }[] = [
  { label: 'Flash (fastest)', value: 'FLASH' },
  { label: 'Turbo', value: 'TURBO' },
  { label: 'Default', value: 'DEFAULT' },
  { label: 'Quality (slowest)', value: 'QUALITY' },
];
const STYLE_TYPE_OPTIONS: { label: string; value: IdeogramStyleType }[] = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'General', value: 'GENERAL' },
  { label: 'Realistic', value: 'REALISTIC' },
  { label: 'Design', value: 'DESIGN' },
  { label: 'Fiction', value: 'FICTION' },
];
const ASPECT_RATIO_OPTIONS: { label: string; value: IdeogramAspectRatio }[] = [
  { label: 'Square (1:1)', value: '1x1' },
  { label: 'Landscape (16:9)', value: '16x9' },
  { label: 'Portrait (9:16)', value: '9x16' },
  { label: 'Landscape (4:3)', value: '4x3' },
  { label: 'Portrait (3:4)', value: '3x4' },
  { label: 'Landscape (3:2)', value: '3x2' },
  { label: 'Portrait (2:3)', value: '2x3' },
];

const MAGIC_PROMPT_LABELS = MAGIC_PROMPT_OPTIONS.map(o => o.label);
const RENDERING_SPEED_LABELS = RENDERING_SPEED_OPTIONS.map(o => o.label);
const STYLE_TYPE_LABELS = STYLE_TYPE_OPTIONS.map(o => o.label);
const ASPECT_RATIO_LABELS = ASPECT_RATIO_OPTIONS.map(o => o.label);

const FULL_WIDTH_STYLE: React.CSSProperties = { width: '100%' };

interface IdeogramGenerateControlPanelProps {
  params: IdeogramGenerateParams;
  setModelParams: (
    updater: (prevParams: { [model: string]: IdeogramGenerateParams }) => { [model: string]: IdeogramGenerateParams }
  ) => void;
  diffusionModel: string;
}

const IdeogramGenerateControlPanel: React.FC<IdeogramGenerateControlPanelProps> = ({
  params, setModelParams, diffusionModel
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback((name: keyof IdeogramGenerateParams, value: unknown) => {
    setModelParams(prevModelParams => {
      // Fall back to defaults so the first edit always works even if params weren't initialized.
      const currentParams = prevModelParams[diffusionModel] ?? getDefaultIdeogramGenerateParams();
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: typeof value === 'string' ? undefined : value,
      value_length: typeof value === 'string' ? value.length : undefined,
      model_name: diffusionModel,
      source_component: 'IdeogramGenerateControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleTextChange = useCallback((
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    updateParam(e.target.name as keyof IdeogramGenerateParams, e.target.value);
  }, [updateParam]);

  const handleAspectRatioChange = useCallback((label: string) => {
    updateParam('aspect_ratio', ASPECT_RATIO_OPTIONS.find(o => o.label === label)?.value ?? '1x1');
  }, [updateParam]);

  const handleMagicPromptChange = useCallback((label: string) => {
    updateParam('magic_prompt', MAGIC_PROMPT_OPTIONS.find(o => o.label === label)?.value ?? 'AUTO');
  }, [updateParam]);

  const handleRenderingSpeedChange = useCallback((label: string) => {
    updateParam('rendering_speed', RENDERING_SPEED_OPTIONS.find(o => o.label === label)?.value ?? 'DEFAULT');
  }, [updateParam]);

  const handleStyleTypeChange = useCallback((label: string) => {
    updateParam('style_type', STYLE_TYPE_OPTIONS.find(o => o.label === label)?.value ?? 'AUTO');
  }, [updateParam]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the image to generate..."
          description="What you want to create from scratch."
          name="prompt"
          value={params.prompt ?? ''}
          onChange={handleTextChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="ideogramAspectRatio"
          label="Aspect Ratio"
          value={ASPECT_RATIO_OPTIONS.find(o => o.value === params.aspect_ratio)?.label ?? 'Square (1:1)'}
          options={ASPECT_RATIO_LABELS}
          onChange={handleAspectRatioChange}
          style={FULL_WIDTH_STYLE}
        />
      </div>

      <div className="mb-4">
        <Select
          id="ideogramMagicPrompt"
          label="Magic Prompt"
          value={MAGIC_PROMPT_OPTIONS.find(o => o.value === params.magic_prompt)?.label ?? 'Auto'}
          options={MAGIC_PROMPT_LABELS}
          onChange={handleMagicPromptChange}
          style={FULL_WIDTH_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Let Ideogram expand and refine your prompt automatically.</p>
      </div>

      <div className="mb-4">
        <Select
          id="ideogramRenderingSpeed"
          label="Rendering Speed"
          value={RENDERING_SPEED_OPTIONS.find(o => o.value === params.rendering_speed)?.label ?? 'Default'}
          options={RENDERING_SPEED_LABELS}
          onChange={handleRenderingSpeedChange}
          style={FULL_WIDTH_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Trades generation speed against final image quality.</p>
      </div>

      <div className="mb-4">
        <Select
          id="ideogramStyleType"
          label="Style Type"
          value={STYLE_TYPE_OPTIONS.find(o => o.value === params.style_type)?.label ?? 'Auto'}
          options={STYLE_TYPE_LABELS}
          onChange={handleStyleTypeChange}
          style={FULL_WIDTH_STYLE}
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="What to avoid (optional)..."
          description="Describe elements you want to exclude from the result."
          name="negative_prompt"
          value={params.negative_prompt ?? ''}
          onChange={handleTextChange}
          maxHeight={300}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>
    </>
  );
};

export default memo(IdeogramGenerateControlPanel);
